import { useState, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useClaimBill } from './useClaimBill.js';

const LABEL_STYLE: React.CSSProperties = { display: 'block', fontWeight: 600, marginBottom: 4 };
const INPUT_STYLE: React.CSSProperties = {
  width: '100%', padding: '8px 12px', borderRadius: 6,
  border: '1px solid var(--color-border-default, #d0d7de)',
  fontSize: 14, boxSizing: 'border-box',
};

export function WithdrawalPage(): ReactNode {
  const { claimId } = useParams<{ claimId: string }>();
  const { bill, error: claimError } = useClaimBill(`/v1/billing/claims/${claimId}`);
  const { token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await apiRequest(`/v1/billing/claims/${claimId}/withdraw`, {
        token, method: 'POST', body: { reason },
      });
      navigate(`/billing/claims/${claimId}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <main aria-label="claim-withdrawal">
      <h1 style={{ marginBottom: 16 }}>{t('billing.withdrawal.title', { bill })}</h1>

      {(error ?? claimError) && <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{error ?? claimError}</p>}

      <form onSubmit={handleSubmit} style={{
        maxWidth: 560, padding: 24, borderRadius: 8,
        border: '1px solid var(--color-border-default, #d0d7de)',
        background: 'var(--color-bg-elevated, #fff)',
      }}>
        <div style={{
          padding: 12, borderRadius: 6, marginBottom: 16,
          background: 'var(--color-warning-bg)', border: '1px solid var(--color-warning)',
        }}>
          <strong>{t('billing.withdrawal.warningLabel')}</strong> {t('billing.withdrawal.warning')}
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={LABEL_STYLE}>{t('billing.withdrawal.reason')}</label>
          <textarea style={{ ...INPUT_STYLE, minHeight: 100 }} required value={reason}
            onChange={(e) => setReason(e.target.value)} data-testid="input-withdrawal-reason"
            placeholder={t('billing.withdrawal.reasonPlaceholder')} />
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button type="submit" disabled={saving} data-testid="btn-withdraw"
            style={{
              padding: '10px 24px', borderRadius: 6, border: 'none', cursor: 'pointer',
              background: 'var(--color-danger)', color: 'var(--color-text-inverse)', fontWeight: 600,
              opacity: saving ? 0.6 : 1,
            }}>
            {saving ? t('billing.withdrawal.withdrawing') : t('billing.withdrawal.confirm')}
          </button>
          <button type="button" onClick={() => navigate(`/billing/claims/${claimId}`)} data-testid="btn-cancel"
            style={{
              padding: '10px 24px', borderRadius: 6, cursor: 'pointer',
              border: '1px solid var(--color-border-default, #d0d7de)',
              background: 'transparent', fontWeight: 600,
            }}>
            {t('billing.withdrawal.cancel')}
          </button>
        </div>
      </form>
    </main>
  );
}
